import React, { Suspense, useEffect, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {jwtDecode} from "jwt-decode";
import LoginContext from "./contexts/loginContext";
import MainLayout from "./layout/MainLayout";
import AuthLayout from "./layout/AuthLayout";
import LoadingSpinner from "./components/UI/loadingSpinner/LoadingSpinner";
import "./scss/App.scss";
import BiometricsEdit from "./pages/BiometricsEdit";

// importing pages
const Login = React.lazy(() => import("./pages/Login"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Churches = React.lazy(() => import("./pages/Churches"));
const Messages = React.lazy(() => import("./pages/Messages"));
const ChurchEdit = React.lazy(() => import("./pages/ChurchEdit"));
const AddChurch = React.lazy(() => import("./pages/AddChurch"));
const Biometrics = React.lazy(() => import("./pages/Biometrics"));
const AddParish = React.lazy(() => import("./pages/AddParish"));
const AddBiometrics = React.lazy(() => import("./pages/AddBiometrics"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const ParishView = React.lazy(() => import("./pages/ParishView"));

const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

const AppContent = () => {
  const navigate = useNavigate();
  const { token, toggleLogin } = useContext(LoginContext);
  
  useEffect(() => {
    if (token) {
      try {
        const decodedToken: { exp: number } = jwtDecode(token);
        const expirationTime = decodedToken.exp * 1000;
        const currentTime = Date.now();
        
        if (expirationTime < currentTime) {
          handleLogout();
        } else {
          const timeout = setTimeout(handleLogout, expirationTime - currentTime);
          return () => clearTimeout(timeout); // Cleanup timeout on unmount
        }
      } catch (error) {
        console.error("Invalid token:", error);
        handleLogout();
      }
    }
  }, [token]);

  const handleLogout = () => {
    toast.error("Session expired. Please log in again.");
    toggleLogin(null); // Correct way to update token state
    navigate("/login");
  };

  return (
    <>
      <ToastContainer />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="/onboarding" element={<Churches />} />
              <Route path="/onboarding/addchurch" element={<AddChurch />} />
              <Route path="/onboarding/addparish" element={<AddParish />} />
              <Route path="/onboarding/edit/:onboardingId" element={<ChurchEdit />} />
              <Route path="/onboarding/view/:onboardingId" element={<ParishView />} />
              <Route path="/biometrics" element={<Biometrics />} />
              <Route path="/biometrics/addbiometrics" element={<AddBiometrics />} />
              <Route path="/biometrics/edit/:biometricsId" element={<BiometricsEdit />} />
              <Route path="/messages" element={<Messages />} />
            </Route>
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;
